import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { rolesAPI, type UniformCheckEngine, type UniformCheckOptions } from '../services/api';

// Shared "Edit Role" modal used by the access matrices in WebAccessPage and
// MobileAccessTab. Both pages inlined this exact markup: an overlay + card with
// a Role Name input, Description textarea, a Geo-based Attendance toggle, a Face
// Verification & Uniform Compliance toggle (roles.aws_face_verification, once
// called "AWS Verification"), and Cancel / Save Changes footer buttons. The only
// structural difference was that the web page rendered an extra "Project-wide
// data access" toggle after it — exposed here as the optional `extraToggles`
// slot so a site that passes nothing renders exactly as before.
// A Multi-store Deployment toggle (roaming promoters) sits after Face
// Verification & Uniform Compliance on both sites.
// With Face Verification & Uniform Compliance on, the role also picks who checks
// uniform & background (roles.uniform_check_engine): Gemini, or Jev with Gemini
// checking any photo Jev can't answer. Whether Jev can be picked is asked of the
// server once per opening (useUniformCheckOptions); the Add Role form uses the
// same choice and hook.

/** The editable role fields shared by both call sites. Sites keep their own
 *  superset state object (web also carries `allProject`) and pass only these
 *  fields plus their own setter through. */
export interface EditRoleModalValue {
  name: string;
  description: string;
  geoBased: boolean;
  awsVerification: boolean;
  /** roles.uniform_check_engine — only shown, and only saved, while awsVerification is on. */
  uniformCheckEngine: UniformCheckEngine;
  /** roles.multi_store_deployment — employees in the role may hold several stores. */
  multiStore: boolean;
}

/** A role row's uniform_check_engine as the choice reads it: anything but 'jev' is Gemini. */
export const uniformCheckEngineOf = (raw: unknown): UniformCheckEngine => (raw === 'jev' ? 'jev' : 'gemini');

/** What the server said about Jev for the project, or that it is still being asked / could not be. */
export type UniformCheckOptionsState =
  | { status: 'loading' }
  | { status: 'failed' }
  | ({ status: 'ready' } & UniformCheckOptions);

// The server answers { success, data }; a bare object is read the same. Anything
// without a true/false `connected` counts as a failed call.
const readUniformCheckOptions = (body: unknown): UniformCheckOptions | null => {
  const wrapped = (body as { data?: unknown } | null | undefined)?.data;
  const raw = (wrapped && typeof wrapped === 'object' ? wrapped : body) as
    | Partial<Record<keyof UniformCheckOptions, unknown>>
    | null
    | undefined;
  if (!raw || typeof raw !== 'object' || typeof raw.connected !== 'boolean') return null;
  return {
    connected: raw.connected,
    rubric: raw.rubric === 'ipro' || raw.rubric === 'default' || raw.rubric === 'project' ? raw.rubric : null,
    reason: typeof raw.reason === 'string' && raw.reason.trim() ? raw.reason : null,
  };
};

/** Asks the server whether Jev can be picked for this project, each time `active`
 *  turns on (the modal opening). No project, an error or an unreadable answer is
 *  'failed': Gemini stays as it is and Jev can't be picked. */
export function useUniformCheckOptions(projectId: number | null | undefined, active = true): UniformCheckOptionsState {
  const [state, setState] = useState<UniformCheckOptionsState>({ status: 'loading' });
  useEffect(() => {
    if (!active) return undefined;
    if (!projectId) {
      setState({ status: 'failed' });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    Promise.resolve()
      .then(() => rolesAPI.getUniformCheckOptions(projectId))
      .then((res) => {
        if (cancelled) return;
        const options = readUniformCheckOptions(res?.data);
        setState(options ? { status: 'ready', ...options } : { status: 'failed' });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, active]);
  return state;
}

const ENGINE_CHOICES: readonly { value: UniformCheckEngine; label: string }[] = [
  { value: 'gemini', label: 'Gemini' },
  { value: 'jev', label: 'Jev' },
];
const FALLBACK_NOTE = "If Jev can't give an answer for a photo, Gemini checks that photo.";
const NO_QUESTION_SET_NOTE = "Jev can't use this project's grooming rules right now.";
const PROJECT_RULES_NOTE = "Jev checks against this project's own grooming rules.";
const OPTIONS_FAILED_NOTE = 'Could not check whether Jev is available.';
const NOT_CONNECTED_NOTE =
  "Jev isn't connected yet: the Sophy key is not set on the server. Until then Gemini checks these photos.";
const FROM_TODAY_NOTE = 'Applies to photos from today; earlier days keep the model they were checked with.';

/** "Uniform & background checked by: Gemini | Jev", drawn under the Face Verification &
 *  Uniform Compliance toggle while it is on. Jev is greyed out, with the reason under the
 *  control, when Jev can't take the project's grooming rules (too long, or not readable
 *  just now) or the server could not be asked, except for a role already on Jev (the roles
 *  API checks only a change INTO Jev). A project on its own prompt is told that Jev checks
 *  against it. */
export function UniformCheckEngineChoice({
  value,
  onChange,
  options,
  savedValue,
}: {
  value: UniformCheckEngine;
  onChange: (engine: UniformCheckEngine) => void;
  options: UniformCheckOptionsState;
  /** The engine in effect for the role when the form opened (Gemini while its toggle was
   *  off). When given, picking another one says it applies from today. */
  savedValue?: UniformCheckEngine;
}) {
  const labelId = useId();
  const reasonId = useId();
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const jevBlockedReason =
    options.status === 'failed'
      ? OPTIONS_FAILED_NOTE
      : options.status === 'ready' && options.rubric === null
        ? options.reason ?? NO_QUESTION_SET_NOTE
        : null;
  // Also held while the server is being asked, without a reason line.
  const jevKept = savedValue === 'jev';
  const jevDisabled = !jevKept && (options.status !== 'ready' || options.rubric === null);
  const notConnected = options.status === 'ready' && options.rubric !== null && !options.connected;
  const ownRules = options.status === 'ready' && options.rubric === 'project';
  // A Jev choice kept while the toggle was off, for a project Jev can no longer check, turns
  // to Gemini when the toggle comes back on, rather than leaving Save to be refused.
  const jevGone = value === 'jev' && !jevKept && options.status === 'ready' && options.rubric === null;
  useEffect(() => {
    if (jevGone) onChange('gemini');
  }, [jevGone, onChange]);
  // One tab stop for the group (the picked choice, or the first pickable one); arrow keys move
  // between the pickable choices and pick as they go, as radio buttons do.
  const pickable = ENGINE_CHOICES.filter((choice) => !(choice.value === 'jev' && jevDisabled));
  const tabStop = pickable.some((choice) => choice.value === value) ? value : pickable[0]?.value;
  const onArrowKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const at = pickable.findIndex((choice) => choice.value === value);
    const next = pickable[(at + step + pickable.length) % pickable.length];
    if (!next || next.value === value) return;
    onChange(next.value);
    buttons.current[ENGINE_CHOICES.indexOf(next)]?.focus();
  };
  return (
    <div className="mt-3 space-y-2 rounded-lg bg-gray-50 px-3 py-2.5">
      <p id={labelId} className="text-xs font-medium text-gray-700">
        Uniform &amp; background checked by
      </p>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        onKeyDown={onArrowKey}
        className="inline-flex overflow-hidden rounded-lg border border-gray-300 bg-white"
      >
        {ENGINE_CHOICES.map((choice, i) => {
          const on = choice.value === value;
          const disabled = choice.value === 'jev' && jevDisabled;
          return (
            <button
              key={choice.value}
              ref={(el) => {
                buttons.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={choice.value === tabStop ? 0 : -1}
              disabled={disabled}
              aria-describedby={disabled && jevBlockedReason ? reasonId : undefined}
              title={disabled ? jevBlockedReason ?? 'Checking whether Jev is available…' : undefined}
              onClick={() => onChange(choice.value)}
              className={`whitespace-nowrap px-3.5 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                i > 0 ? 'border-l border-gray-300' : ''
              } ${on ? 'bg-primary-50 font-semibold text-primary-800 ring-1 ring-inset ring-primary-600' : 'text-gray-700 hover:bg-gray-50 disabled:hover:bg-white'}`}
            >
              {choice.label}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-gray-500">{FALLBACK_NOTE}</p>
      {jevBlockedReason && (
        <p id={reasonId} className="text-xs text-gray-500">
          {jevBlockedReason}
        </p>
      )}
      {ownRules && <p className="text-xs text-gray-500">{PROJECT_RULES_NOTE}</p>}
      {notConnected && <p className="text-xs text-amber-700">{NOT_CONNECTED_NOTE}</p>}
      {savedValue !== undefined && value !== savedValue && (
        <p className="text-xs text-primary-700">{FROM_TODAY_NOTE}</p>
      )}
    </div>
  );
}

interface EditRoleModalProps<T extends EditRoleModalValue> {
  /** The role being edited. Rendering reads name/description/geoBased/
   *  awsVerification/uniformCheckEngine/multiStore; the generic `T` lets each
   *  site pass its own superset. */
  value: T;
  /** Replaces the edited role object. Each site passes its own
   *  `setEditingRole`. Toggles spread `{ ...value, <field>: !value.<field> }`. */
  onChange: (next: T) => void;
  /** Closes the modal (X button + Cancel button). */
  onClose: () => void;
  /** Persists the edit (Save Changes button). */
  onSave: () => void;
  /** Disables Save and shows the inline spinner while a save is in flight. */
  saving: boolean;
  /** The role's project: asked once on opening whether Jev can check its photos.
   *  Without one, Jev can't be picked. */
  projectId?: number | null;
  /** Optional extra toggle rows rendered after the Face Verification & Uniform
   *  Compliance toggle (web's "Project-wide data access" toggle). Sites that
   *  omit it render exactly as before. */
  extraToggles?: ReactNode;
}

export function EditRoleModal<T extends EditRoleModalValue>({
  value,
  onChange,
  onClose,
  onSave,
  saving,
  projectId,
  extraToggles,
}: EditRoleModalProps<T>) {
  const uniformCheckOptions = useUniformCheckOptions(projectId);
  // The engine in effect when the modal opened (Jev only while the toggle is on, as the
  // server's history counts it): picking another says it applies from today.
  const [savedUniformCheckEngine] = useState<UniformCheckEngine>(
    value.awsVerification && value.uniformCheckEngine === 'jev' ? 'jev' : 'gemini',
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-gray-900">Edit Role</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Role Name</label>
            <input
              type="text"
              value={value.name}
              onChange={(e) => onChange({ ...value, name: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
            <textarea
              value={value.description}
              onChange={(e) => onChange({ ...value, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Geo-based Attendance</label>
              <p className="text-xs text-gray-500">Sign in at any store marks daily attendance</p>
            </div>
            <button
              type="button"
              onClick={() => onChange({ ...value, geoBased: !value.geoBased })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value.geoBased ? 'bg-primary-600' : 'bg-gray-200'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value.geoBased ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Face Verification &amp; Uniform Compliance</label>
                <p className="text-xs text-gray-500">AWS face match, and the uniform &amp; background check, during attendance</p>
              </div>
              <button
                type="button"
                onClick={() => onChange({ ...value, awsVerification: !value.awsVerification })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value.awsVerification ? 'bg-primary-600' : 'bg-gray-200'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value.awsVerification ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            {value.awsVerification && (
              <UniformCheckEngineChoice
                value={value.uniformCheckEngine}
                onChange={(engine) => onChange({ ...value, uniformCheckEngine: engine })}
                options={uniformCheckOptions}
                savedValue={savedUniformCheckEngine}
              />
            )}
          </div>
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Multi-store Deployment</label>
              <p className="text-xs text-gray-500">Can be mapped to several stores at once (roaming promoters)</p>
            </div>
            <button
              type="button"
              onClick={() => onChange({ ...value, multiStore: !value.multiStore })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value.multiStore ? 'bg-primary-600' : 'bg-gray-200'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value.multiStore ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          {extraToggles}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving || !value.name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditRoleModal;
